import { useEffect, useState } from "react";
import { makeFetchRequest } from "../../utilities/common";
import { defaultUrl } from "../../features/actions";
import { User, Mail, Phone, Book, Pencil, Calendar, Users } from "lucide-react";

export default function First({ nextStep, handleChange, form }: any) {
  const [classrooms, setClassrooms] = useState<any[]>([]);

  useEffect(() => {
    const fetchClassrooms = async () => {
      const response = await makeFetchRequest(
        `${defaultUrl}/classroom`,
        false,
        true,
      );
      setClassrooms(response || []);
    };
    fetchClassrooms();
  }, []);

  const inputClass =
    "w-full h-12 px-4 bg-slate-50 border-2 border-transparent rounded-xl focus:border-blue-500 focus:bg-white outline-none transition-all text-slate-700 font-medium placeholder:text-slate-400";
  const labelClass =
    "text-xs font-black text-slate-500 uppercase tracking-widest mb-2 block ml-1";

  return (
    <div className="p-8 md:p-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center gap-3 mb-8">
        <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl">
          <User size={24} />
        </div>
        <div>
          <h2 className="text-2xl font-black text-slate-800">Staff Profile</h2>
          <p className="text-slate-400 text-sm font-medium">
            Basic identity and professional credentials.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="space-y-2">
          <label className={labelClass}>
            Surname <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="surname"
            value={form.surname}
            onChange={handleChange}
            placeholder="Surname"
            className={inputClass}
            required
          />
        </div>
        <div className="space-y-2">
          <label className={labelClass}>
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="firstname"
            value={form.firstname}
            onChange={handleChange}
            placeholder="First Name"
            className={inputClass}
            required
          />
        </div>
        <div className="space-y-2">
          <label className={labelClass}>
            Other Names <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="othername"
            value={form.othername}
            onChange={handleChange}
            placeholder="Other Names"
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div className="space-y-2">
          <label className={labelClass}>
            Email Address <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Mail
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              size={18}
            />
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="staff@gmail.com"
              className={`${inputClass} pl-12`}
              required
            />
          </div>
        </div>
        <div className="space-y-2">
          <label className={labelClass}>
            Mobile Number <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Phone
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              size={18}
            />
            <input
              type="text"
              name="phone_number"
              value={form.phone_number}
              onChange={handleChange}
              placeholder="+234..."
              className={`${inputClass} pl-12`}
              required
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        <div className="space-y-2">
          <label className={labelClass}>
            Gender <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
            {["male", "female"].map((g) => (
              <button
                key={g}
                type="button"
                onClick={() =>
                  handleChange({ target: { name: "gender", value: g } })
                }
                className={`flex-1 py-2 rounded-lg font-bold text-sm capitalize transition-all ${form.gender === g ? "bg-white text-blue-600 shadow-sm" : "text-slate-400"}`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <label className={labelClass}>
            Date of Birth <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Calendar
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              size={18}
            />
            <input
              type="date"
              name="date_of_birth"
              value={form.date_of_birth}
              onChange={handleChange}
              className={`${inputClass} pl-12`}
              required
            />
          </div>
        </div>
        <div className="space-y-2">
          <label className={labelClass}>
            Assign Classroom <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Users
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              size={18}
            />
            <select
              name="classroom_id"
              value={form.classroom_id}
              onChange={handleChange}
              className={`${inputClass} pl-12 appearance-none`}
              required
            >
              <option value="">Select Class</option>
              {classrooms.map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        <div className="space-y-2">
          <label className={labelClass}>
            Qualification & Discipline <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <select
              name="degree"
              value={form.degree}
              onChange={handleChange}
              className={`${inputClass} flex-1`}
              required
            >
              <option value="">Choose degree</option>
              <option>Graduate</option>
              <option>Masters</option>
              <option>Postgraduate Diploma</option>
              <option>PhD</option>
            </select>
            <input
              type="text"
              name="discipline"
              value={form.discipline}
              onChange={handleChange}
              placeholder="Discipline (e.g. Maths)"
              className={`${inputClass} flex-1`}
              required
            />
          </div>
        </div>
        <div className="space-y-2">
          <label className={labelClass}>
            Digital Signature <span className="text-red-500">*</span>
          </label>
          <div className="relative group">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-hover:text-blue-500">
              <Pencil size={18} />
            </div>
            <input
              type="file"
              name="signature"
              onChange={handleChange}
              className={`${inputClass} pl-12 pt-2.5 file:hidden`}
              required
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={nextStep}
          className="w-full md:w-auto px-12 h-14 bg-blue-600 text-white rounded-2xl font-black uppercase tracking-widest shadow-xl shadow-blue-200 hover:bg-blue-700 transition-all"
        >
          Continue to Step 2
        </button>
      </div>
    </div>
  );
}
